import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { productMutations } from '@as-react/common-queries';
import { ErrorMessage, NotifyMeError } from '@as-react/common-types';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon, EnvelopeSimpleIcon, XIcon } from '@yonderland/andes-icons';
import { Banner, Box, ButtonAlpha, Icon, IconButton, Link, StackBlock, TextAlpha, TextField, } from '@yonderland/andes-react';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { useOAuth } from '../../hooks/useOAuth';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { productActions } from '../../redux/product/actions/ProductActions';
import { makeGetProductInfo, makeGetSelectedSku } from '../../redux/product/selectors/ProductSelector';
import { FormField } from '../form/FormField';
import { EmailValidationWrapper } from '../form/validators/EmailValidationWrapper';
import { CONTAINER_PADDING } from '../product-detail/delivery-promise-extended/DeliveryPromiseExtended';
const emailFieldName = 'notifyMeEmail';
export const NOTIFY_ME_FORM_NAME = 'notify-me';
export const NotifyMeForm = ({ withNewDesign }) => {
    var _a;
    const { t } = useTranslation([I18N_NAMESPACES.account, I18N_NAMESPACES.general, I18N_NAMESPACES.product]);
    const { defaultRequestParams, servicesEndpoint, privacyPolicyLink } = useAemConfig();
    const [submittedEmail, setSubmittedEmail] = useState();
    const [emailFieldFocused, setEmailFieldFocused] = useState(false);
    const [shouldNotResetStateOnSizeChange, setShouldNotResetStateOnSizeChange] = useState(false);
    const { userInfo } = useOAuth();
    const dispatch = useDispatch();
    const initialEmail = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.email) !== null && _a !== void 0 ? _a : '';
    const selectedSku = useSelector(makeGetSelectedSku());
    const { productId } = useSelector(makeGetProductInfo());
    const { postNotifyMeSubscribe } = productMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: postNotifyMeSubscribeMutation, isPending, isError } = useMutation(postNotifyMeSubscribe);
    useEffect(() => {
        if (shouldNotResetStateOnSizeChange) {
            setShouldNotResetStateOnSizeChange(false);
        }
        else {
            setSubmittedEmail(undefined);
            setEmailFieldFocused(false);
            formProps.reset();
        }
    }, [selectedSku]);
    const formProps = useForm({
        defaultValues: {
            notifyMeEmail: initialEmail,
        },
        mode: 'onTouched',
    });
    useEffect(() => {
        if (initialEmail && !formProps.formState.dirtyFields.notifyMeEmail) {
            formProps.setValue(emailFieldName, initialEmail);
        }
    }, [initialEmail]);
    const handleSubmit = (_a) => __awaiter(void 0, [_a], void 0, function* ({ notifyMeEmail }) {
        if (submittedEmail === notifyMeEmail || isPending) {
            return;
        }
        if (!selectedSku) {
            setShouldNotResetStateOnSizeChange(true);
            dispatch(productActions.setNotifyMeError(NotifyMeError.SELECT_SIZE));
            return;
        }
        postNotifyMeSubscribeMutation({
            productId: Number(productId),
            skuId: Number(selectedSku),
            email: notifyMeEmail,
        }, {
            onSuccess: () => {
                dispatch(analyticsActions.notifyMeRequest());
                setSubmittedEmail(notifyMeEmail);
            },
        });
    });
    const handleClearEmail = () => {
        formProps.setValue(emailFieldName, '');
    };
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), noValidate: true, name: NOTIFY_ME_FORM_NAME, children: _jsxs(StackBlock, { gap: "2", children: [_jsx(Box, { marginInline: withNewDesign ? `-${CONTAINER_PADDING}` : undefined, children: isError ? (_jsx(Banner, { status: "error", text: t(ErrorMessage.API_ERROR) })) : submittedEmail ? (_jsx(Banner, { status: "success", text: t('product:notifyMe.success.message', { email: submittedEmail }) })) : (_jsx(Banner, { status: "informative", text: t('product:notifyMe.instruction') })) }), _jsx(EmailValidationWrapper, { emailFieldKey: emailFieldName, errorMessages: {
                            invalidEmail: t('account.subscribe.invalid'),
                            required: t('account.subscribe.invalid'),
                        }, children: ({ validate, renderError }) => (_jsx(FormField, { name: emailFieldName, validate: validate, renderError: renderError, children: (_a) => {
                                var _b = _a.fieldProps, { ref: _ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                                return (_jsx(TextField, Object.assign({}, options, { label: t('product:notifyMe.email.label'), testId: "notify_me_email_address", id: "notify_me_email_address", autoComplete: "email", error: error, maxLength: 100, placeholder: t('product:notifyMe.email.placeholder'), adornmentEnd: !emailFieldFocused && options.value === submittedEmail ? (_jsx(Icon, { source: CheckIcon })) : (options.value && (_jsx(IconButton, { variant: "plain", icon: { source: XIcon }, size: "sm", onClick: handleClearEmail }))) })));
                            } })) }), _jsx(ButtonAlpha, { testId: "notify_me_submit_button", theme: "branded", variant: "primary", size: "md", fullWidth: true, type: "submit", state: isPending ? 'loading' : 'interactive', text: t('product:notifyMe.submit.button'), iconLeft: {
                            source: EnvelopeSimpleIcon,
                            size: 'md',
                        } }), Boolean(privacyPolicyLink) && (_jsxs(TextAlpha, { variant: "bodyS", color: "subtle", children: [t('product:notifyMe.privacy.policy.part1'), _jsx(Link, { href: privacyPolicyLink, size: "sm", variant: "secondary", target: "_blank", children: t('product:notifyMe.privacy.policy.part2') }), t('product:notifyMe.privacy.policy.part3')] }))] }) }) })));
};
